/**
 * User Profile Service
 * API client for user profile operations with full error handling
 */

import type {
  UserProfile,
  UserProfileUpdate,
  UserPreferences,
  UserPreferencesUpdate,
  PasswordChangeRequest,
  ActivityHistory,
  ActivityFilter,
} from "@/types/user";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:8000/api/v1";

function getAccessToken(): string | null {
  try {
    const stored = localStorage.getItem("seweb_auth");
    if (stored) {
      const authData = JSON.parse(stored);
      return authData.access_token || null;
    }
    return null;
  } catch {
    return null;
  }
}

async function request<T>(url: string, options: RequestInit = {}): Promise<T> {
  const token = getAccessToken();

  const defaultHeaders: HeadersInit = {
    "Content-Type": "application/json",
  };

  if (token) {
    defaultHeaders["Authorization"] = `Bearer ${token}`;
  }

  const response = await fetch(url, {
    ...options,
    headers: {
      ...defaultHeaders,
      ...(options.headers || {}),
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: "Unknown error" }));
    throw new Error(error.detail || `HTTP ${response.status}`);
  }

  return response.json();
}

export const userProfileService = {
  // Profile endpoints
  async getMyProfile(): Promise<UserProfile> {
    return request<UserProfile>(`${API_URL}/users/me/profile`);
  },

  async updateMyProfile(data: Partial<UserProfileUpdate>): Promise<UserProfile> {
    return request<UserProfile>(`${API_URL}/users/me`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  },

  // Password endpoint
  async changeMyPassword(data: PasswordChangeRequest): Promise<{ message: string }> {
    return request<{ message: string }>(`${API_URL}/users/me/change-password`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  },

  // Preferences endpoints
  async getMyPreferences(): Promise<UserPreferences> {
    return request<UserPreferences>(`${API_URL}/users/me/preferences`);
  },

  async updateMyPreferences(data: Partial<UserPreferencesUpdate>): Promise<UserPreferences> {
    return request<UserPreferences>(`${API_URL}/users/me/preferences`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  },

  // Activity history endpoint
  async getMyActivityHistory(
    page: number = 1,
    pageSize: number = 20,
    filters?: ActivityFilter
  ): Promise<ActivityHistory> {
    const params = new URLSearchParams({
      page: page.toString(),
      page_size: pageSize.toString(),
    });

    if (filters?.action_type) {
      params.append("action_type", filters.action_type);
    }
    if (filters?.resource_type) {
      params.append("resource_type", filters.resource_type);
    }

    return request<ActivityHistory>(`${API_URL}/users/me/activity-history?${params.toString()}`);
  },
};
